import { ManagerAuthorization } from '@common/enums';
import { assertHasAuthorization } from '@frontend/utils';
import type { IAuthorization } from '../../interfaces/authorization';
import type { IServiceConsumer } from '../../interfaces/serviceConsumer';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import {
    AdminTable,
    DeleteButton,
    EditButton,
    FullName,
    Header,
    Link,
    LinkButton,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../elements/common';
import { Component } from '../../elements/Component';
import { NullableFormattedDate } from '../../elements/NullableFormattedDate';

interface IAuthedServiceConsumer extends IServiceConsumer {
    authorization: IAuthorization;
}

interface IServiceConsumerListProps {
    serviceId: string;
}

interface IServiceConsumerListState {
    authorization: IAuthorization;
    isSearching: boolean;
    serviceConsumers: IAuthedServiceConsumer[];
}

export class ServiceConsumerList extends Component<IServiceConsumerListProps, IServiceConsumerListState> {
    constructor(props: IServiceConsumerListProps, context?: any) {
        super(props, context);
        this.state = {
            serviceConsumers: [],
            isSearching: true,
            authorization: {},
        };

        assertHasAuthorization(ManagerAuthorization.serviceRightsCareproviders);
    }

    public async componentDidMount() {
        await this.search(this.props.serviceId);
    }

    public async componentDidUpdate(prev: IServiceConsumerListProps) {
        if (this.props.serviceId !== prev.serviceId) {
            await this.search(this.props.serviceId);
        }
    }

    public render() {
        return (
            <>
                <Header
                    actionButtons={[
                        <LinkButton
                            icon="icon icon-add"
                            to={`/serviceConsumers/add?serviceId=${this.props.serviceId}`}
                        >
                            {translateKey('common.addItem', translateKey('serviceConsumer.serviceConsumer'))}
                        </LinkButton>,
                    ]}
                    content={translateKey('serviceConsumer.serviceConsumers')}
                />
                <AdminTable>
                    <TableHead>
                        <TableRow>
                            <TableHeader>{translateKey('organization.organization')}</TableHeader>
                            <TableHeader>{translateKey('practitioner.practitioner')}</TableHeader>
                            <TableHeader>{translateKey('common.startDate')}</TableHeader>
                            <TableHeader>{translateKey('common.endDate')}</TableHeader>
                            {this.state.authorization.canUpdate && <TableHeader align="right"></TableHeader>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {this.state.serviceConsumers.map((serviceConsumer) => (
                            <TableRow key={serviceConsumer.id}>
                                <TableData>
                                    <Link
                                        to={`/organizations/${serviceConsumer.employment.organization.id}?tab=consumers`}
                                    >
                                        {serviceConsumer.employment.organization.name}
                                    </Link>
                                </TableData>
                                <TableData>
                                    <Link
                                        to={`/practitioners/${serviceConsumer.employment.practitioner.id}?tab=servicesConsumingListIsVisible`}
                                    >
                                        <FullName {...serviceConsumer.employment.practitioner} />
                                    </Link>
                                </TableData>
                                <TableData>
                                    <NullableFormattedDate value={serviceConsumer.startDate} />
                                </TableData>
                                <TableData>
                                    <NullableFormattedDate value={serviceConsumer.endDate} />
                                </TableData>
                                {this.state.authorization.canUpdate && (
                                    <TableData align="right">
                                        {serviceConsumer.authorization.canUpdate && (
                                            <EditButton
                                                to={`/serviceConsumers/${serviceConsumer.id}?serviceId=${this.props.serviceId}`}
                                            />
                                        )}
                                        {serviceConsumer.authorization.canUpdate &&
                                            serviceConsumer.endDate === null && (
                                                <DeleteButton onClick={() => this.remove(serviceConsumer.id)} />
                                            )}
                                    </TableData>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </AdminTable>
            </>
        );
    }

    private async search(serviceId: string) {
        const autorizationQuery = 'includeItemAuthorizations=canUpdate&includeListAuthorizations=canAdd';
        const url = `/api/serviceConsumers?serviceId=${serviceId}&${autorizationQuery}`;
        await this.setState({ isSearching: true });
        const result = await fetchJson(url, 'GET');
        let canUpdate = false;
        if (result.json.items) {
            if (
                result.json.items.find(
                    (item: IAuthedServiceConsumer) => item.authorization && item.authorization.canUpdate
                )
            ) {
                canUpdate = true;
            }
            await this.setState({ serviceConsumers: result.json.items, isSearching: false });
        }
        const authorization = {
            canUpdate: canUpdate,
            canAdd: result.json.authorization ? result.json.authorization.canAdd : false,
        };
        await this.setState({ authorization: authorization });
    }

    private async remove(id: string) {
        const url = `/api/serviceConsumers/${id}`;
        await this.setState({ isSearching: true });
        await fetchJson(url, 'POST', { endDateIsCurrentDate: true });
        await this.search(this.props.serviceId);
    }
}
